import { useState, useMemo, useRef } from 'react'
import { useAuthStore, CloverTokenizationService, isCardAlreadyOnFile } from '@restaurant/core'
import type { CardDetails } from '@restaurant/core'
import { apiClient } from '@restaurant/api-client'
import type { CloverInstance, ClientUser } from '@restaurant/core'
import { safeGetLogger } from '@restaurant/logging'

interface UseCloverCardAddOptions {
  /** Clover merchant ID for this restaurant location */
  merchantId: string
  /**
   * Called once the card is on file. The saved CardDetails is passed when a
   * fresh save produced it; it is omitted on the idempotent "already on file"
   * path (the card exists but this call didn't create it). Callers use it only
   * to trigger a card-list refresh, so the argument is optional.
   */
  onSuccess: (card?: CardDetails) => void
}

interface UseCloverCardAddReturn {
  /** PAKMS API key needed to initialize the Clover.js SDK; null until fetched. */
  pakmsKey: string | null
  /** True while fetchPakmsKey() is in flight. */
  isLoadingPakmsKey: boolean
  /** User-friendly error message if PAKMS key fetch fails; null otherwise. */
  pakmsError: string | null
  /** True while submitCard() is processing the full tokenization + save flow. */
  isSubmitting: boolean
  /** User-facing error message for tokenization or card-save failures; null otherwise. */
  submitError: string | null
  /**
   * Fetch the PAKMS API access key from the backend.
   * Call this once when the card-add UI mounts, before initializing Clover.js.
   */
  fetchPakmsKey: () => Promise<void>
  /**
   * Run the full card-add flow:
   * 1. Tokenize the card fields via clover.createToken()
   * 2. Lazily create a Clover customer if one doesn't exist for this merchant
   * 3. Persist the customer ID to user metadata (non-blocking)
   * 4. Save the token against the customer via the Flask API
   * 5. Call onSuccess with the saved CardDetails
   *
   * @param clover - A Clover SDK instance (from useCloverJs.initClover())
   */
  submitCard: (clover: CloverInstance) => Promise<void>
}

/**
 * useCloverCardAdd
 *
 * Orchestrates the Clover iFrame tokenization card-add flow for the web app.
 *
 * Responsibilities:
 * - PAKMS key fetching with loading/error state
 * - Card tokenization error handling (CARD_NUMBER, CARD_DATE, CARD_CVV, etc.)
 * - Lazy Clover customer creation when no clover_customer_id exists for this merchant
 * - Non-blocking clover_customer_id persistence to Supabase user metadata
 * - Auth store update with the new customer ID for future skip-creation optimisation
 * - Card save and onSuccess callback for triggering parent refresh
 *
 * Architecture: delegates all API calls to CloverTokenizationService; owns only
 * UI state (loading flags, error strings) and the lazy-creation / persistence logic.
 */
export function useCloverCardAdd({
  merchantId,
  onSuccess,
}: UseCloverCardAddOptions): UseCloverCardAddReturn {
  const { user } = useAuthStore()

  const [pakmsKey, setPakmsKey] = useState<string | null>(null)
  const [isLoadingPakmsKey, setIsLoadingPakmsKey] = useState(false)
  const [pakmsError, setPakmsError] = useState<string | null>(null)

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  // Single-flight gate. A ref (not the isSubmitting state) because React state
  // is stale for a second call arriving before a re-render. Without it, two
  // concurrent submits both run removeExistingCards → saveCard and the loser
  // 409s on Clover's one-card-per-customer rule (prod 2026-09-28).
  const inFlightRef = useRef(false)

  // Stable service instance — recreated only if user.id changes (same pattern as useCartSync)
  const service = useMemo(
    () =>
      new CloverTokenizationService({
        apiClient,
        userId: user?.id,
      }),
    [user?.id]
  )

  // ─── fetchPakmsKey ──────────────────────────────────────────────────────────

  const fetchPakmsKey = async (): Promise<void> => {
    setIsLoadingPakmsKey(true)
    setPakmsError(null)

    try {
      const key = await service.fetchPakmsKey(merchantId)
      setPakmsKey(key)
    } catch {
      setPakmsError('Unable to initialize payment form. Please try again.')
      setPakmsKey(null)
    } finally {
      setIsLoadingPakmsKey(false)
    }
  }

  // ─── submitCard ─────────────────────────────────────────────────────────────

  const submitCard = async (clover: CloverInstance): Promise<void> => {
    // Drop a duplicate submit already in flight — the first one owns the save.
    if (inFlightRef.current) return
    inFlightRef.current = true
    setIsSubmitting(true)
    setSubmitError(null)

    try {
      // Step 1: Tokenize the card fields
      const tokenResult = await clover.createToken()

      if (tokenResult.errors && Object.keys(tokenResult.errors).length > 0) {
        // Use the first error value as the user-facing message
        const firstError = Object.values(tokenResult.errors)[0]
        setSubmitError(firstError ?? 'Card tokenization failed. Please check your card details and try again.')
        return
      }

      if (!tokenResult.token) {
        setSubmitError('Card tokenization failed. Please check your card details and try again.')
        return
      }

      const cardToken = tokenResult.token

      // Step 2: Lazy customer creation
      const existingCustomerId = (user?.metadata?.pos_customer_ids as Record<string, string> | undefined)?.[merchantId]

      let customerId: string

      if (existingCustomerId) {
        // Reuse the existing Clover customer ID
        customerId = existingCustomerId
      } else {
        // Create a new Clover customer
        const newCustomer = await service.createCustomer(merchantId, {
          firstName: user?.firstName ?? '',
          lastName: user?.lastName ?? '',
          email: user?.email ?? '',
          userId: user?.id ?? '',
          phoneNumber: user?.phoneNumber ?? undefined,
        })
        customerId = newCustomer.id

        // Update the auth store with the new customer ID (merge into metadata.pos_customer_ids)
        const existingPosIds = (user?.metadata?.pos_customer_ids as Record<string, string> | undefined) ?? {}
        useAuthStore.getState().updateProfile({
          metadata: { ...user?.metadata, pos_customer_ids: { ...existingPosIds, [merchantId]: customerId } },
        } satisfies Partial<ClientUser>)

        // Step 3: Persist the customer ID to Supabase user metadata — non-blocking.
        // Retry once on failure. The lazy reconciliation endpoint acts as the
        // ultimate safety net if both attempts fail.
        try {
          await service.persistCustomerId(merchantId, customerId)
        } catch {
          setTimeout(async () => {
            try {
              await service.persistCustomerId(merchantId, customerId)
            } catch {
              safeGetLogger()?.error('persistCustomerId failed after retry', {
                domain: 'checkout',
                merchantId,
                customerId,
                userId: user?.id,
              })
            }
          }, 2000)
        }
      }

      // Step 4: Remove existing cards (Clover only supports one card on file)
      await service.removeExistingCards(merchantId, customerId)

      // Step 5: Save the new card against the customer
      const savedCard = await service.saveCard(merchantId, customerId, cardToken)

      // Step 6: Notify parent to refresh the card list
      onSuccess(savedCard)
    } catch (err: unknown) {
      // The card is already on file (409). It IS saved — treat as success and
      // refresh rather than showing the shopper a card error.
      if (isCardAlreadyOnFile(err)) {
        onSuccess()
        return
      }
      const message = err instanceof Error ? err.message : 'Failed to save card. Please try again.'
      setSubmitError(message)
    } finally {
      inFlightRef.current = false
      setIsSubmitting(false)
    }
  }

  return {
    pakmsKey,
    isLoadingPakmsKey,
    pakmsError,
    isSubmitting,
    submitError,
    fetchPakmsKey,
    submitCard,
  }
}
